import { PaletteMode } from '@mui/material';
import { createTheme, SimplePaletteColorOptions, ThemeOptions } from '@mui/material/styles';
import { Shadows } from '@mui/material/styles/shadows';
import { buttonStyle } from './buttonStyles';
import {
  black,
  blue,
  green,
  grey,
  grey11,
  grey13,
  grey2,
  grey4,
  red2,
  white,
  yellow,
} from './colors';
import { colors } from './themeColors';

export type ColorMode = PaletteMode | 'system';

export const px4 = '0.25rem';
export const px6 = '0.375rem';
export const px8 = '0.5rem';
export const px9 = '0.5625rem';
export const px10 = '0.625rem';
export const px12 = '0.75rem';
export const px14 = '0.875rem';
export const px16 = '1rem';
export const px18 = '1.125rem';
export const px20 = '1.25rem';
export const px22 = '1.375rem';
export const px24 = '1.5rem';
export const px28 = '1.75rem';
export const px32 = '2rem';
export const px34 = '2.125rem';
export const px36 = '2.25rem';
export const px38 = '2.375rem';
export const px40 = '2.5rem';
export const px42 = '2.625rem';
export const px48 = '3rem';
export const px52 = '3.25rem';
export const px54 = '3.375rem';
export const px64 = '4rem';
export const px66 = '4.125rem';
export const px92 = '5.75rem';
export const px100 = '6.25rem';
export const px109 = '6.8125rem';
export const px168 = '10.5rem';
export const px172 = '10.75rem';
export const px198 = '12.375rem';
export const px200 = '12.5rem';
export const px290 = '18.125rem';
export const px368 = '23rem';
export const px424 = '26.5rem';

declare module '@mui/material/styles' {
  interface Palette {
    normal: SimplePaletteColorOptions;
    tertiary: Palette['primary'];
    link: Palette['primary'];
  }

  interface PaletteOptions {
    normal?: SimplePaletteColorOptions;
    tertiary?: PaletteOptions['primary'];
    link?: PaletteOptions['primary'];
  }
}

export const getTheme = () => {
  return createTheme({
    palette: {
      mode: 'light',

      base: colors.base,
      primary: {
        main: blue,
      },
      brand: {
        main: colors.brand[600],
        ...colors.brand,
      },
      success: {
        main: green,
        ...colors.success,
      },
      secondary: {
        main: black,
        light: black,
        dark: black,
      },
      tertiary: {
        main: grey11,
      },
      link: {
        main: 'transparent',
      },
      background: {
        default: white,
        paper: white,
      },
      surface: {
        primary: colors.base[0], // Base/0 light, Base/900 dark
        secondary: colors.base[100], // Base/100 light, Base/600 dark
        tertiary: colors.base[200], // Base/200 light, Base/600 dark
        brand: colors.brand[50], // Brand/50 light, Brand/900 dark
        success: colors.success[50], // Success/50 light, Success/800 dark
        warning: colors.warning[50], // Warning/50 light, Warning/800 dark
        error: colors.error[50], // Error/50 light, Error/900 dark
        info: colors.info[50], // Info/50 light, Info/800 dark
      },
      text: {
        primary: black,
        secondary: black,
        quaternary: green,
        brand: colors.brand[600], // Brand/600 light & dark
        success: colors.success[500], // Success/500 light & dark
        warning: colors.warning[500], // Warning/500 light & dark
        error: colors.error[600], // Error/600 light & dark
        info: colors.info[600], // Info/600 light & dark
      },
      border: {
        primary: colors.base[200], // Base/200 light, Base/700 dark
        secondary: colors.base[700], // Base/700 light, Base/600 dark
        tertiary: colors.base[0], // Base/0 light, Base/900 dark
        brand: colors.brand[600], // Brand/600 light & dark
        brandSecondary: colors.brand[200], // Brand/200 light & dark
        success: colors.success[300], // Success/300 light & dark
        warning: colors.warning[300], // Warning/300 light & dark
        error: colors.error[300], // Error/300 light & dark
        error2: colors.error[600], // Error/600 light & dark
        info: colors.info[300], // Info/300 light & dark
      },
      error: {
        main: colors.error[600],
        ...colors.error,
      },
      warning: {
        main: colors.warning[600],
        ...colors.warning,
      },
      info: {
        main: colors.info[600],
        ...colors.info,
      },
      element: {
        // Default states
        primaryBackground: colors.base[100], // Base/100 light, Base/800 dark
        secondaryBackground: colors.base[0], // Base/0 light, Base/900 dark
        brandBackground: colors.brand[600], // Brand/600 light & dark
        successBackground: colors.success[600], // Success/600 light & dark
        warningBackground: colors.warning[600], // Warning/600 light & dark
        errorBackground: colors.error[600], // Error/600 light & dark
        infoBackground: colors.info[600], // Info/600 light & dark
        blackBackground: colors.base[900], // Base/900 light & dark

        // Hover states
        primaryBackgroundHover: colors.base[50], // Base/50 light, Base/700 dark
        secondaryBackgroundHover: colors.base[100], // Base/100 light, Base/600 dark
        brandBackgroundHover: colors.brand[500], // Brand/500 light & dark
        successBackgroundHover: colors.success[500], // Success/500 light & dark
        warningBackgroundHover: colors.warning[500], // Warning/500 light & dark
        errorBackgroundHover: colors.error[500], // Error/500 light & dark
        infoBackgroundHover: colors.info[500], // Info/500 light & dark
        blackBackgroundHover: colors.base[800], // Base/800 light & dark

        // Pressed states
        primaryBackgroundPressed: colors.base[200], // Base/200 light, Base/700 dark
        secondaryBackgroundPressed: colors.base[100], // Base/100 light, Base/600 dark
        brandBackgroundPressed: colors.brand[700], // Brand/700 light & dark
        successBackgroundPressed: colors.success[700], // Success/700 light & dark
        warningBackgroundPressed: colors.warning[700], // Warning/700 light & dark
        errorBackgroundPressed: colors.error[700], // Error/700 light & dark
        infoBackgroundPressed: colors.info[700], // Info/700 light & dark
        blackBackgroundPressed: colors.base[1000], // Base/1000 light & dark

        // Inactive states
        primaryBackgroundInactive: colors.base[300], // Base/300 light, Base/700 dark
        secondaryBackgroundInactive: colors.base[50], // Base/50 light, Base/700 dark
        brandBackgroundInactive: colors.brand[300], // Brand/300 light, Brand/700 dark
        successBackgroundInactive: colors.success[50], // Success/50 light, Success/800 dark
        warningBackgroundInactive: colors.warning[50], // Warning/50 light, Warning/800 dark
        errorBackgroundInactive: colors.error[50], // Error/50 light, Error/900 dark
        infoBackgroundInactive: colors.info[50], // Info/50 light, Info/800 dark
        blackBackgroundInactive: colors.base[700], // Base/700 light, Base/900 dark
      },
      // normal: {
      //   main: 'rgb(255, 226, 71)',
      //   light: 'rgb(255, 252, 237)',
      //   dark: 'rgb(102, 90, 28)',
      // },
      // sign-up: {
      //   main: 'rgb(92, 200, 255)',
      //   light: 'rgb(239, 249, 255)',
      //   dark: 'rgb(37, 80, 102)',
      // },
      // divider: dark ? "#fff" : '#333333',
      // action: {
      //     hoverOpacity: 0.3,
      // },
    },

    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        // most basic recommended timing
        standard: 300,
        // this is to be used in complex animations
        complex: 375,
        // recommended when something is entering screen
        enteringScreen: 225,
        // recommended when something is leaving screen
        leavingScreen: 195,
      },
      easing: {
        // This is the most common easing curve.
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        // Objects enter the screen at full velocity from off-screen and
        // slowly decelerate to a resting point.
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        // Objects leave the screen at full velocity. They do not decelerate when off-screen.
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        // The sharp curve is used by objects that may return to the screen at any time.
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
          focusRipple: false,
        },
      },
      MuiBadge: {
        styleOverrides: {
          colorPrimary: yellow,
          root: {
            '& .MuiBadge-badge': {
              backgroundColor: yellow,
            },
          },
        },
      },
      ...buttonStyle,
      // MuiIconButton: {
      //   defaultProps: {
      //     size: 'medium',
      //     color: 'primary',
      //   },
      //   styleOverrides: {
      //     root: {
      //       padding: '15px',
      //       borderRadius: '12px',
      //       backgroundColor: white,
      //       '& .MuiSvgIcon-root': {
      //         height: '18px',
      //         width: '18px',
      //       },
      //     },
      //     colorPrimary: {
      //       border: `1px solid ${blue}`,
      //     },
      //     colorSecondary: {
      //       border: `1px solid ${black}`,
      //     },
      //   },
      // },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              '& fieldset': {
                borderColor: `${grey4}`,
                borderRadius: '10px',
              },

              '& .MuiInputAdornment-positionStart': {
                marginRight: '-4px',
              },
              '& .MuiInputAdornment-positionEnd': {
                marginLeft: '-4px',
              },

              '& .Mui-error': {
                '& fieldset.MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${red2}`,
                },
              },
            },
            '& .MuiInputBase-input': {
              padding: '12px 16px',
              color: black,
              backgroundColor: white,
              borderRadius: '10px',

              '&::placeholder': {
                color: grey,
                opacity: 'unset',
              },
            },
            '& .MuiInputLabel-root': {
              top: '3px',
              color: grey,

              '& .MuiInputLabel-asterisk': {
                color: red2,
              },
            },
            '& .Mui-error': {
              color: red2,
            },
            '& .MuiFormHelperText-root': {
              color: grey,
              '&.Mui-error': {
                color: red2,
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            height: '48px',

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: grey4,
            },

            '&.Mui-disabled': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(20, 25, 31, 0.10)',
              },

              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(20, 25, 31, 0.10)',
                cursor: 'not-allowed',
              },
            },

            '&.Mui-error': {
              '& .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${red2}`,
              },

              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: grey,
              },
            },

            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${grey}`,
              },

              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: grey,
              },
            },

            '& fieldset': {
              borderColor: grey4,
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: grey,
            },

            '& .MuiInputBase-input': {
              padding: '14px 16px',
              color: black,
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
              fontFamily: 'Yelo',
              fontWeight: 400,

              '&::placeholder': {
                color: grey,
                opacity: 'unset',
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: '-3px 3px 12px 0px #2D2C2C0F!important',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: grey,
            backgroundColor: white,
            '&.Mui-selected': {
              color: blue,
              backgroundColor: white,
              '&:hover': {
                backgroundColor: white,
              },
            },
            '&:hover': {
              color: blue,
              backgroundColor: white,
            },
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          color: 'text.primary',
          fontFamily: 'Yelo',
        },
      },
      // MuiTable: {
      //   defaultProps: {
      //     stickyHeader: true,
      //   },
      //   styleOverrides: {
      //     root: {
      //       borderCollapse: 'separate',
      //       borderSpacing: '0px 6px',
      //       minWidth: '650px',
      //     },
      //   },
      // },
      // MuiTableRow: {
      //   styleOverrides: {
      //     root: {
      //       backgroundColor: white,
      //       border: `1px solid ${grey1}`,
      //       boxShadow: '-3px 3px 12px 0px #ADAFC017',
      //     },
      //   },
      // },
      // MuiTableHead: {
      //   styleOverrides: {
      //     root: {
      //       backgroundColor: white,
      //       border: `1px solid ${grey1}`,
      //
      //       '& .MuiTableCell-root': {
      //         fontStyle: 'normal',
      //         fontWeight: 600,
      //         fontSize: '12px',
      //         lineHeight: '20px',
      //         color: grey7,
      //         padding: '11px',
      //         textTransform: 'uppercase',
      //         userSelect: 'none',
      //
      //         '&:first-of-type': {
      //           borderTopLeftRadius: '12px!important',
      //           borderBottomLeftRadius: '12px!important',
      //           paddingLeft: '22px',
      //         },
      //
      //         '&:last-of-type': {
      //           borderTopRightRadius: '12px!important',
      //           borderBottomRightRadius: '12px!important',
      //           paddingRight: '22px',
      //         },
      //       },
      //     },
      //   },
      // },
      // MuiTableCell: {
      //   styleOverrides: {
      //     root: {
      //       fontStyle: 'normal',
      //       fontWeight: 400,
      //       fontSize: '14px',
      //       lineHeight: '22px',
      //       color: black,
      //       border: 'none',
      //       padding: '18px',
      //       width: 'calc(100% - 20px)',
      //       marginBottom: '40px',
      //       '&:first-of-type': {
      //         borderTopLeftRadius: '18px!important',
      //         borderBottomLeftRadius: '18px!important',
      //         paddingLeft: '24px',
      //       },
      //
      //       '&:last-of-type': {
      //         borderTopRightRadius: '18px!important',
      //         borderBottomRightRadius: '18px!important',
      //         paddingRight: '24px',
      //       },
      //     },
      //   },
      // },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: grey13,
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            '& ul': {
              '& li': {
                '&:first-of-type': {
                  // border: '1px solid red',
                  marginRight: '24px',
                },

                '&:last-of-type': {
                  // border: '1px solid green',
                  marginLeft: '24px',
                },
              },
            },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            padding: 0,
            minWidth: 'unset',
            '&.MuiPaginationItem-root': {
              backgroundColor: 'unset',
              padding: 0,
              minWidth: 'unset',

              '&.MuiPaginationItem-page': {
                color: grey,
                '&.Mui-selected': {
                  color: black,
                },
              },

              '&.MuiPaginationItem-previousNext': {
                width: '36px',
                height: '36px',
                borderRadius: '12px',
                background: white,

                '&.Mui-disabled': {
                  backgroundColor: grey2,
                },
              },
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: white,
            minHeight: 'unset',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            borderRadius: '11px',
            border: `1px solid transparent`,
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '22px',
            color: black,
            padding: '12px 24px',
            minHeight: 'unset',
            minWidth: '90px',
            textTransform: 'unset',

            '&.Mui-selected': {
              color: blue,
              backgroundColor: 'transparent',
              fontWeight: '600',
            },
          },
        },
      },
    },

    typography: {
      fontFamily: 'Yelo, Poppins, Arial, sans-serif',
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontStyle: 'normal',
        lineHeight: '30px',
        fontWeight: 700,
        fontSize: '28px',
        color: black,
      },
      h2: {
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '24px',
        color: black,
      },
      h3: {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '22px',
        color: black,
      },
      // h4: {
      //   fontSize: px18,
      //   fontWeight: 'bold',
      //   lineHeight: px24,
      // },
      // body1: {
      //   fontSize: px16,
      //   fontWeight: 'normal',
      //   lineHeight: px22,
      // },
      // body2: {
      //   fontSize: px14,
      //   fontWeight: 'normal',
      //   lineHeight: px20,
      // },
      // subtitle1: {
      //   fontSize: px16,
      //   fontWeight: 'bold',
      //   lineHeight: px22,
      // },
      // subtitle2: {
      //   fontSize: px16,
      //   fontWeight: 'bold',
      //   lineHeight: px22,
      // },
      // caption: {
      //   fontSize: px12,
      //   fontWeight: 'normal',
      //   lineHeight: px16,
      //   letterSpacing: '0.4px',
      // },
      // overline: {
      //   fontSize: px12,
      //   fontWeight: 'normal',
      //   lineHeight: px16,
      //   letterSpacing: '1px',
      //   textTransform: 'uppercase',
      // },
    },
    shape: {
      borderRadius: 10,
    },
    shadows: Array(25).fill('none') as Shadows,
    // spacing: 4,
    breakpoints: {
      values: {
        xs: 0,
        sm: 576,
        md: 992,
        lg: 1300,
        xl: 1536,
      },
    },
  } as ThemeOptions);
};

export { useTheme } from '@mui/material/styles';
